"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosStockDailyAggregateSchema = exports.APosAgentSuggestionSchema = exports.APosSuggestionPrioritySchema = exports.APosSuggestionStatusSchema = exports.APosSuggestionTypeSchema = exports.APosVelocityTrendSchema = exports.APosAnomalyTypeSchema = exports.APosQuantityBucketSchema = exports.APosStockOperationRecordSchema = exports.APosStockOperationTypeSchema = exports.APosStockOperationStatusSchema = exports.APosStockEventSchema = exports.APosStockEventReferenceTypeSchema = exports.APosStockEventMovementTypeSchema = exports.APosStockOperationSchema = void 0;
const zod_1 = require("zod");
const id_schema_1 = require("../../common/id.schema");
exports.APosStockOperationSchema = zod_1.z.enum([
    'add', 'subtract', 'set',
    'reserve', 'release_reserve',
    'damage', 'dispose',
    'return_receive', 'return_restock', 'return_damage',
    'return_to_bin', 'restock_from_bin', 'damage_from_bin', 'vendor_return',
]);
exports.APosStockEventMovementTypeSchema = zod_1.z.enum([
    'grn_receipt', 'sale', 'transfer_in', 'transfer_out',
    'count', 'adjustment', 'damage_report', 'disposal',
    'return_received', 'return_restocked', 'return_damaged',
    'reservation', 'reservation_release', 'marketplace_order',
    'po_receipt', 'transfer_damage', 'stock_reset',
    'return_to_bin', 'restock_from_bin', 'damage_from_bin', 'vendor_return',
    // Kitchen stock (recipe depletion, waste log, prep batches)
    'recipe_use', 'recipe_use_reversal', 'waste', 'prep_input', 'prep_output',
]);
exports.APosStockEventReferenceTypeSchema = zod_1.z.enum([
    'grn', 'order', 'transfer', 'transfer_invoice', 'count', 'purchase_order',
    'adjustment', 'damage', 'return', 'exchange', 'marketplace_order', 'online_order', 'stock_reset',
    // Kitchen stock
    'recipe', 'waste', 'prep',
]);
exports.APosStockEventSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    locationId: id_schema_1.EntityIdSchema,
    variantId: id_schema_1.EntityIdSchema,
    productId: id_schema_1.EntityIdSchema,
    operation: exports.APosStockOperationSchema,
    // 'set' may legitimately target 0 (e.g. negative-stock reset). All other
    // operations require quantity > 0 — enforced by the refine below.
    quantity: zod_1.z.number().nonnegative(),
    movementType: exports.APosStockEventMovementTypeSchema,
    referenceType: exports.APosStockEventReferenceTypeSchema,
    referenceId: id_schema_1.EntityIdSchema,
    secondaryReferenceType: exports.APosStockEventReferenceTypeSchema.optional(),
    secondaryReferenceId: id_schema_1.EntityIdSchema.optional(),
    unitCost: zod_1.z.number().nonnegative().optional(),
    totalCost: zod_1.z.number().nonnegative().optional(),
    userId: id_schema_1.EntityIdSchema,
    userName: zod_1.z.string().min(1),
    notes: zod_1.z.string().max(500).optional(),
    sourceService: zod_1.z.string().min(1),
    eventId: zod_1.z.string().min(1),
    batchOperationId: zod_1.z.string().optional(),
    /** Use-by date (epoch ms) of the stock this 'add' brings in — kept on its FIFO cost layer. Kitchen only. */
    expiryDate: zod_1.z.number().int().positive().optional(),
    timestamp: zod_1.z.number(),
}).refine((e) => e.operation === 'set' || e.quantity > 0, { message: 'Number must be greater than 0', path: ['quantity'] });
exports.APosStockOperationStatusSchema = zod_1.z.enum([
    'processing', 'completed', 'completed_with_errors', 'failed',
]);
exports.APosStockOperationTypeSchema = zod_1.z.enum([
    'grn_complete', 'transfer_ship', 'transfer_receive',
    'count_complete', 'bulk_adjustment',
]);
exports.APosStockOperationRecordSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    operationId: id_schema_1.EntityIdSchema,
    type: exports.APosStockOperationTypeSchema,
    sourceReferenceId: id_schema_1.EntityIdSchema,
    totalItems: zod_1.z.number().int().nonnegative(),
    processedItems: zod_1.z.number().int().nonnegative().default(0),
    failedItems: zod_1.z.number().int().nonnegative().default(0),
    status: exports.APosStockOperationStatusSchema.default('processing'),
    errors: zod_1.z.array(zod_1.z.object({
        eventId: zod_1.z.string(),
        variantId: zod_1.z.string(),
        error: zod_1.z.string(),
        timestamp: zod_1.z.number(),
    })).default([]),
    startedAt: zod_1.z.number(),
    completedAt: zod_1.z.number().optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
});
exports.APosQuantityBucketSchema = zod_1.z.enum([
    'stockQuantity', 'damagedQuantity', 'returnedQuantity', 'reservedQuantity', 'returnBinQuantity',
]);
exports.APosAnomalyTypeSchema = zod_1.z.enum([
    'unusual_quantity', 'negative_stock', 'off_hours',
    'frequency_spike', 'shrinkage_pattern',
]);
exports.APosVelocityTrendSchema = zod_1.z.enum([
    'accelerating', 'stable', 'declining',
]);
exports.APosSuggestionTypeSchema = zod_1.z.enum([
    'reorder', 'transfer', 'price_adjustment', 'damage_review', 'count_suggested',
]);
exports.APosSuggestionStatusSchema = zod_1.z.enum([
    'pending', 'approved', 'rejected', 'executed', 'expired',
]);
exports.APosSuggestionPrioritySchema = zod_1.z.enum([
    'high', 'medium', 'low',
]);
exports.APosAgentSuggestionSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    suggestionId: id_schema_1.EntityIdSchema,
    agentId: zod_1.z.string().min(1),
    agentVersion: zod_1.z.string().min(1),
    type: exports.APosSuggestionTypeSchema,
    targetKey: zod_1.z.string().min(1),
    action: zod_1.z.object({
        suggestedQty: zod_1.z.number().optional(),
        supplierId: zod_1.z.string().optional(),
        estimatedCost: zod_1.z.number().nonnegative().optional(),
        reason: zod_1.z.string().min(1),
        dataPoints: zod_1.z.object({
            currentStock: zod_1.z.number(),
            avgDailySales: zod_1.z.number(),
            daysOfStock: zod_1.z.number(),
            lastGRNDate: zod_1.z.string().optional(),
            supplierLeadTimeDays: zod_1.z.number().optional(),
        }),
        confidence: zod_1.z.number().min(0).max(1),
    }),
    status: exports.APosSuggestionStatusSchema.default('pending'),
    priority: exports.APosSuggestionPrioritySchema,
    expiresAt: zod_1.z.number(),
    approvedBy: id_schema_1.EntityIdSchema.optional(),
    approvedAt: zod_1.z.number().optional(),
    rejectedReason: zod_1.z.string().optional(),
    executionResult: zod_1.z.record(zod_1.z.unknown()).optional(),
    createdAt: zod_1.z.number(),
});
exports.APosStockDailyAggregateSchema = zod_1.z.object({
    pk: zod_1.z.string(),
    sk: zod_1.z.string(),
    tenantId: id_schema_1.TenantIdSchema,
    variantId: id_schema_1.EntityIdSchema,
    productId: id_schema_1.EntityIdSchema,
    locationId: id_schema_1.EntityIdSchema,
    date: zod_1.z.string(),
    gsi1pk: zod_1.z.string().optional(),
    gsi1sk: zod_1.z.string().optional(),
    grnQuantity: zod_1.z.number().int().default(0),
    saleQuantity: zod_1.z.number().int().default(0),
    transferInQuantity: zod_1.z.number().int().default(0),
    transferOutQuantity: zod_1.z.number().int().default(0),
    adjustmentQuantity: zod_1.z.number().int().default(0),
    damageQuantity: zod_1.z.number().int().default(0),
    returnReceivedQuantity: zod_1.z.number().int().default(0),
    returnRestockedQuantity: zod_1.z.number().int().default(0),
    countAdjustmentQuantity: zod_1.z.number().int().default(0),
    grnCostTotal: zod_1.z.number().default(0),
    saleCostTotal: zod_1.z.number().default(0),
    saleRevenueTotal: zod_1.z.number().default(0),
    transactionCount: zod_1.z.number().int().default(0),
    grnCount: zod_1.z.number().int().default(0),
    transferCount: zod_1.z.number().int().default(0),
    closingStockQuantity: zod_1.z.number().int().default(0),
    closingDamagedQuantity: zod_1.z.number().int().default(0),
    closingReturnedQuantity: zod_1.z.number().int().default(0),
    closingReturnBinQuantity: zod_1.z.number().int().default(0),
    returnBinReceivedQuantity: zod_1.z.number().int().default(0),
    returnBinReceivedValue: zod_1.z.number().default(0),
    restockedFromBinQuantity: zod_1.z.number().int().default(0),
    restockedFromBinValue: zod_1.z.number().default(0),
    damagedFromBinQuantity: zod_1.z.number().int().default(0),
    damagedFromBinValue: zod_1.z.number().default(0),
    vendorReturnQuantity: zod_1.z.number().int().default(0),
    vendorReturnValue: zod_1.z.number().default(0),
    avgDailySales_7d: zod_1.z.number().optional(),
    avgDailySales_30d: zod_1.z.number().optional(),
    daysOfStock: zod_1.z.number().optional(),
    turnoverRate: zod_1.z.number().optional(),
    velocityTrend: exports.APosVelocityTrendSchema.optional(),
    reorderSuggested: zod_1.z.boolean().optional(),
    reorderSuggestedQty: zod_1.z.number().int().optional(),
    updatedAt: zod_1.z.number(),
});
//# sourceMappingURL=stock-event.schema.js.map